import {
  ActivityChangeOptions,
  ActivityItem,
  ActivityTagItem,
} from '@spotify/plugin-confidence-activity-react';

import { createWorkflowUpdatesChangeExtension } from './workflow-updates-change';

interface StatsData {
  alpha?: number;
  power?: number;
  fallbackSampleSize?: number;
  testHorizonStrategy?: 'FIXED_HORIZON' | 'SEQUENTIAL';
  [key: string]: unknown;
}

const getAlphaLabel = (alpha: number | undefined) => {
  if (alpha === undefined) {
    return '';
  }

  const alphaValue = alpha * 100;
  switch (alphaValue) {
    case 1:
      return 'Low (1%)';
    case 5:
      return 'Standard (5%)';
    case 10:
      return 'High (10%)';
    default:
      return `Custom (${alphaValue}%)`;
  }
};

const getPowerLabel = (power: number | undefined) => {
  if (power === undefined) {
    return '';
  }

  const powerValue = power * 100;
  switch (powerValue) {
    case 90:
      return 'High (90%)';
    case 80:
      return 'Standard (80%)';
    default:
      return `Custom (${powerValue}%)`;
  }
};

const getStrategyLabel = (strategy?: string) => {
  if (strategy === 'FIXED_HORIZON') {
    return 'Upon conclusion';
  }
  if (strategy === 'SEQUENTIAL') {
    return 'Continuously';
  }
  return '';
};

const StatsChangeRender = ({ change }: ActivityChangeOptions) => {
  const oldValue = change.oldValue as StatsData | null;
  const newValue = change.newValue as StatsData | null;

  const oldStrategy = oldValue?.testHorizonStrategy;
  const newStrategy = newValue?.testHorizonStrategy;
  const strategyChanged = oldStrategy !== newStrategy;

  const alphaChanged = oldValue?.alpha !== newValue?.alpha;
  const powerChanged = oldValue?.power !== newValue?.power;

  return (
    <>
      {strategyChanged && (
        <ActivityItem.Section label="Updated results strategy">
          <ActivityTagItem
            oldValue={getStrategyLabel(oldStrategy)}
            newValue={getStrategyLabel(newStrategy)}
          />
        </ActivityItem.Section>
      )}

      {alphaChanged && (
        <ActivityItem.Section label="Updated alpha">
          <ActivityTagItem
            oldValue={getAlphaLabel(oldValue?.alpha)}
            newValue={getAlphaLabel(newValue?.alpha)}
          />
        </ActivityItem.Section>
      )}

      {powerChanged && (
        <ActivityItem.Section label="Updated power">
          <ActivityTagItem
            oldValue={getPowerLabel(oldValue?.power)}
            newValue={getPowerLabel(newValue?.power)}
          />
        </ActivityItem.Section>
      )}
    </>
  );
};

export const STATS_CHANGE_EXTENSION = createWorkflowUpdatesChangeExtension({
  id: 'stats-change',
  predicate: ({ change }) => {
    if (change.fieldPath !== '/module_data/stats') {
      return false;
    }

    const oldValue = change.oldValue as StatsData | null;
    const newValue = change.newValue as StatsData | null;

    // Show if any of these changed
    return (
      oldValue?.testHorizonStrategy !== newValue?.testHorizonStrategy ||
      oldValue?.alpha !== newValue?.alpha ||
      oldValue?.power !== newValue?.power
    );
  },
  render: StatsChangeRender,
});
